import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Title,
  Card,
  Text,
  Stack,
  Group,
  Badge,
  Button,
  Loader,
  Alert,
  Image,
  SimpleGrid,
  FileButton,
  ActionIcon,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconExclamationCircle,
  IconInfoCircle,
  IconUpload,
  IconX,
  IconRosetteDiscountCheck,
} from '@tabler/icons-react';
import { useAuth } from '@clerk/react';
import { notifications } from '@mantine/notifications';
import { useApiClient, useLiveQuery, useSession } from '../../api';
import { uploadBuyerIdVerification, fetchProtectedBlobUrl } from '../../api/uploads';
import ProtectedRoute from '../../components/auth/ProtectedRoute';

// Verification status enum (contract §1.5) — same palette the admin seller
// verification view uses for these statuses.
const STATUS_META = {
  not_submitted: { color: 'gray', label: 'Not submitted' },
  pending: { color: 'yellow', label: 'Pending review' },
  under_review: { color: 'pink', label: 'Under review' },
  approved: { color: 'green', label: 'Approved' },
  rejected: { color: 'red', label: 'Rejected' },
  resubmission_required: { color: 'orange', label: 'Resubmission required' },
};

// The three form slots map onto the `kind` values of the file routes (§4.23).
const FILE_SLOTS = [
  { kind: 'selfie', field: 'selfieWithId', label: 'Selfie with ID', hint: 'Hold your ID next to your face' },
  { kind: 'front', field: 'frontOfId', label: 'Front of ID', hint: 'All text clearly readable' },
  { kind: 'back', field: 'backOfId', label: 'Back of ID', hint: 'Barcode side of your ID' },
];

// Buyer ID verification (contract §4.23) — optional, badge-only. Kept live on
// the caller's account topic: verification.status_changed fires there when an
// admin reviews the submission, so approval flips the UI without a refresh.
const BuyerVerificationPage = () => {
  const { isSignedIn } = useAuth();
  const api = useApiClient();
  const { me } = useSession();
  const accountTopic = me && `user.${me.clerk_user_id}.account`;

  const { data: statusData, loading: statusLoading, error: statusError, refetch: refetchStatus } = useLiveQuery(
    '/api/v1/me/buyer-verification/status',
    undefined,
    { enabled: !!isSignedIn, topics: [accountTopic] }
  );
  const status = statusData?.status ?? null;

  // Full Verification row (§3.13) — 404 when nothing was ever submitted, so
  // only fetch once the status says a row exists.
  const { data: verification, refetch: refetchVerification } = useLiveQuery(
    '/api/v1/me/buyer-verification',
    undefined,
    {
      enabled: !!isSignedIn && !!status && status !== 'not_submitted',
      topics: [accountTopic],
    }
  );

  // Already-uploaded documents are auth-gated binaries — fetched as blobs and
  // shown via object URLs, revoked on unmount / resubmission.
  const [fileUrls, setFileUrls] = useState({});
  useEffect(() => {
    if (!verification) return undefined;

    let cancelled = false;
    const urls = {};
    FILE_SLOTS.filter(({ kind }) => verification.files?.[kind]).forEach(({ kind }) => {
      fetchProtectedBlobUrl(api, `/api/v1/me/buyer-verification/files/${kind}`)
        .then((url) => {
          if (cancelled) {
            // Effect already cleaned up — nothing will ever render this URL.
            URL.revokeObjectURL(url);
            return;
          }
          urls[kind] = url;
          setFileUrls((prev) => ({ ...prev, [kind]: url }));
        })
        .catch((err) => console.error(`Failed to load ${kind} image:`, err));
    });

    return () => {
      cancelled = true;
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
      setFileUrls({});
    };
  }, [api, verification]);

  // Upload form state: local files with object-URL previews.
  const [pendingFiles, setPendingFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const pendingRef = useRef(pendingFiles);
  pendingRef.current = pendingFiles;

  // Revoke any leftover local previews on unmount.
  useEffect(
    () => () => {
      Object.values(pendingRef.current).forEach((f) => URL.revokeObjectURL(f.preview));
    },
    []
  );

  const selectFile = useCallback((field, file) => {
    if (!file) return;
    setPendingFiles((prev) => {
      if (prev[field]) URL.revokeObjectURL(prev[field].preview);
      return { ...prev, [field]: { file, preview: URL.createObjectURL(file) } };
    });
  }, []);

  const removeFile = useCallback((field) => {
    setPendingFiles((prev) => {
      if (!prev[field]) return prev;
      URL.revokeObjectURL(prev[field].preview);
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setSubmitError(null);
      await uploadBuyerIdVerification(api, {
        selfieWithId: pendingFiles.selfieWithId?.file,
        frontOfId: pendingFiles.frontOfId?.file,
        backOfId: pendingFiles.backOfId?.file,
      });
      Object.values(pendingFiles).forEach((f) => URL.revokeObjectURL(f.preview));
      setPendingFiles({});
      notifications.show({
        title: 'Documents submitted',
        message: 'Your ID is now pending review.',
        color: 'green',
      });
      // Resubmission resets status to pending and replaces the files (§4.23).
      refetchStatus();
      refetchVerification();
    } catch (err) {
      setSubmitError(err?.message || 'Failed to submit ID verification. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusMeta = STATUS_META[status] || STATUS_META.not_submitted;
  const hasPendingFile = FILE_SLOTS.some(({ field }) => pendingFiles[field]);
  const showRejection =
    (status === 'rejected' || status === 'resubmission_required') && verification?.rejection_reason;

  return (
    <ProtectedRoute requireAuth={true}>
      <Container size="sm" py="xl">
        <Stack gap="xl">
          <Group gap="xs" align="center">
            <IconShieldCheck size="1.5rem" color="var(--mantine-color-blue-6)" />
            <Title order={1}>ID Verification</Title>
          </Group>

          <Alert color="blue" variant="light" icon={<IconInfoCircle size="1rem" />}>
            Verification is optional — nothing is locked without it. Once approved,
            sellers see a <strong>Verified buyer</strong> badge next to your name in
            messages and special requests.
          </Alert>

          {statusError && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {statusError.message}
            </Alert>
          )}

          {statusLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader size="lg" />
            </div>
          ) : status && (
            <>
              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={600}>Verification status</Text>
                    <Badge
                      color={statusMeta.color}
                      variant="light"
                      size="lg"
                      leftSection={
                        status === 'approved' ? <IconRosetteDiscountCheck size="0.9rem" /> : undefined
                      }
                    >
                      {statusMeta.label}
                    </Badge>
                  </Group>

                  {showRejection && (
                    <Alert color={statusMeta.color} variant="light" title="Reviewer feedback">
                      {verification.rejection_reason}
                    </Alert>
                  )}

                  {status === 'approved' && (
                    <Text size="sm" c="dimmed">
                      Your identity is verified. Sellers now see the badge in your
                      conversations and special requests.
                    </Text>
                  )}

                  {verification && (
                    <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
                      {FILE_SLOTS.map(({ kind, label }) => (
                        <Stack key={kind} gap={4}>
                          <Text size="xs" c="dimmed">{label}</Text>
                          {fileUrls[kind] ? (
                            <Image src={fileUrls[kind]} alt={label} radius="sm" h={120} fit="cover" />
                          ) : (
                            <Text size="sm" c="dimmed">
                              {verification.files?.[kind] ? 'Loading…' : 'Not uploaded'}
                            </Text>
                          )}
                        </Stack>
                      ))}
                    </SimpleGrid>
                  )}
                </Stack>
              </Card>

              <Card shadow="sm" padding="lg" radius="md" withBorder>
                <Stack gap="md">
                  <div>
                    <Text fw={600}>
                      {status === 'not_submitted' ? 'Submit your documents' : 'Resubmit documents'}
                    </Text>
                    <Text size="sm" c="dimmed">
                      Upload photos of your government-issued ID. Any file you provide
                      replaces the previous one and puts your submission back in the
                      review queue.
                    </Text>
                  </div>

                  {submitError && (
                    <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
                      {submitError}
                    </Alert>
                  )}

                  <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
                    {FILE_SLOTS.map(({ field, label, hint }) => {
                      const pending = pendingFiles[field];
                      return (
                        <Stack key={field} gap={4}>
                          <Text size="sm" fw={500}>{label}</Text>
                          {pending ? (
                            <div style={{ position: 'relative' }}>
                              <Image src={pending.preview} alt={label} radius="sm" h={120} fit="cover" />
                              <ActionIcon
                                color="red"
                                size="sm"
                                variant="filled"
                                aria-label={`Remove ${label}`}
                                onClick={() => removeFile(field)}
                                style={{ position: 'absolute', top: 4, right: 4 }}
                              >
                                <IconX size="0.8rem" />
                              </ActionIcon>
                            </div>
                          ) : (
                            <Text size="xs" c="dimmed">{hint}</Text>
                          )}
                          <FileButton onChange={(file) => selectFile(field, file)} accept="image/*">
                            {(props) => (
                              <Button
                                {...props}
                                variant={pending ? 'outline' : 'light'}
                                size="xs"
                                leftSection={<IconUpload size="0.9rem" />}
                              >
                                {pending ? 'Replace' : 'Select image'}
                              </Button>
                            )}
                          </FileButton>
                        </Stack>
                      );
                    })}
                  </SimpleGrid>

                  <Group justify="flex-end">
                    <Button
                      onClick={handleSubmit}
                      disabled={!hasPendingFile}
                      loading={submitting}
                      leftSection={<IconShieldCheck size="1rem" />}
                    >
                      Submit for review
                    </Button>
                  </Group>
                </Stack>
              </Card>
            </>
          )}
        </Stack>
      </Container>
    </ProtectedRoute>
  );
};

export default BuyerVerificationPage;
